import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { StepHeader, MemberTokenWarning } from '@bingads-webui-campaign-react/linkedin-shared-components';
import { useFormService, FORM_STATES } from '@bingads-webui-fluent-component/form';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';

import { CampaignPanel } from './components/campaign-panel';
import { CompanyPageCard } from './components/company-page-card';
import { LinkedInAdSettingsShell } from './components/linkedin-ad-settings';
import { AdPreviewer } from './components/ad-previewer';
import { useAdPreviewState } from './use-ad-preview-state';
import { getAdStepStyle } from './styles/linkedIn-ad-styles';
import { useAdPreviewData } from './use-ad-preview-data';
import { LinkedInAdFooter } from './components/linkedin-ad-footer';

const EnableMultiAd = true;

const getTitleAndDescription = (isAdEditor, isEditing) => {
  if (!isAdEditor) {
    return ({
      title: _TL_('Create ads'),
      description: _TL_('Provide the information and assets you want to include in your ads.'),
    });
  }

  return ({
    title: isEditing ? _TL_('Edit ad') : _TL_('Create ad'),
    description: _TL_('Provide the information and assets you want to include in your ad.'),
  });
};

export const LinkedInAdStep = (props) => {
  const {
    isAdEditor,
    data,
    onDataChange,
    error,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAdStepStyle);

  const {
    companyInfo,
    campaign = {},
    adData = {},
    isMemberTokenRevoked,
  } = data || {};

  const {
    serverErrors = [],
  } = error || {};

  const { state } = useFormService();
  const { context: { saveFailed } } = state;
  const isSaving = state.matches(FORM_STATES.SAVING);

  const showAllValidationErrors = !_.isEmpty(serverErrors) || saveFailed;
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const UnmappedFormServerErrors = withControlErrorHandling(UnmappedFormErrors);

  const adPreviewState = useAdPreviewState(adData, onDataChange);
  const adPreviewData = useAdPreviewData(i18n, adData, companyInfo);

  const {
    title: stepTitle,
    description: stepDescription,
  } = useMemo(() => getTitleAndDescription(isAdEditor, !!adData.Id), [isAdEditor, adData.Id]);

  if (_.isEmpty(data)) {
    return null;
  }

  return (
    <div className={classNames('linkedin-ad-step', classes.container)}>
      <div>
        <StepHeader
          title={stepTitle}
          description={stepDescription}
        />
      </div>
      {isAdEditor && isMemberTokenRevoked && <MemberTokenWarning /> }
      <div className={classNames(classes.content)}>
        <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
              <div className={classNames('linkedin-ad-left-section', classes.leftStyles)}>
                <div className={classes.formContainer}>
                  <UnmappedFormServerErrors className="unmapped-server-errors" />
                  {!isAdEditor && (
                    <CampaignPanel
                      campaign={campaign}
                      onDataChange={onDataChange}
                    />
                  )}
                  {!isAdEditor && (
                    <CompanyPageCard
                      companyInfo={companyInfo}
                    />
                  )}
                  <LinkedInAdSettingsShell
                    adData={adData}
                    adPreviewData={adPreviewData}
                    adPreviewState={adPreviewState}
                    onDataChange={onDataChange}
                    isAdEditor={isAdEditor && !!adData.Id}
                  />
                </div>
              </div>
              <div className={classNames('linkedin-ad-right-section', classes.rightStyles)}>
                <AdPreviewer
                  adData={adData}
                  adPreviewData={adPreviewData}
                  adPreviewState={adPreviewState}
                  isAdEditor={isAdEditor && !!adData.Id}
                />
              </div>
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>
      {(EnableMultiAd && !isAdEditor) && <LinkedInAdFooter />}
    </div>
  );
};

LinkedInAdStep.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({}).isRequired,
  }).isRequired,
  isAdEditor: PropTypes.bool,
  error: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};

LinkedInAdStep.defaultProps = {
  isAdEditor: false,
};
